/**
 * Entry point for the embeddable widget bundle.
 *
 * Two integration modes:
 *
 *   A. Copy-paste (recommended) — anonymous session minted on the fly:
 *
 *        <script
 *          src="https://YOUR-BACKEND/widget-cdn/helpdesk.js"
 *          data-api-key="hd_pk_xxx"
 *          data-backend="https://YOUR-BACKEND"
 *          defer
 *        ></script>
 *
 *      The loader POSTs `data-api-key` to `<backend>/api/widget/anon-session`,
 *      receives a short-lived JWT, and boots the widget. The visitor's
 *      anonymous id is persisted in localStorage so re-visits stitch into
 *      the same end-customer record.
 *
 *   B. Identity-verified — host backend signs a per-user JWT:
 *
 *        <script
 *          src="..."
 *          data-service="svc_xxx"
 *          data-jwt="..."
 *          data-api="https://YOUR-BACKEND"
 *        ></script>
 *
 * Supported data-* attributes:
 *   data-api-key             → public key (mode A)
 *   data-backend             → backend origin (mode A)
 *   data-service             → service-id (mode B)
 *   data-jwt                 → tenant-signed JWT (mode B)
 *   data-api                 → api/backend URL (mode B alias)
 *   data-realtime            → realtime URL (optional)
 *   data-locale              → locale (en|es)
 *   data-primary-color       → branding override (playground)
 *   data-logo-url            → branding override (playground)
 *   data-position            → branding override (playground)
 *   data-greeting            → branding override (playground)
 *   data-assistant-name      → assistant persona override
 *   data-assistant-avatar    → assistant avatar URL override
 *   data-assistant-tagline   → assistant tagline override
 *   data-widget-shape        → FAB shape: circle|rounded-square|pill
 *   data-widget-size         → FAB size: sm|md|lg
 *   data-widget-animation    → FAB animation: none|pulse|bounce|breath|wave
 *   data-widget-image        → FAB image/GIF URL
 *   data-welcome-message     → welcome bubble text
 *   data-welcome-timeout     → welcome bubble auto-hide seconds (0 = pinned)
 */

import { mount, unmount } from 'svelte';
import HelpDeskWidget from './HelpDeskWidget.svelte';
import type {
  Locale,
  WidgetAnimation,
  WidgetPosition,
  WidgetShape,
  WidgetSize,
} from './lib/types';

interface AutoInitDataset {
  service?: string;
  jwt?: string;
  apiKey?: string;
  backend?: string;
  api?: string;
  realtime?: string;
  locale?: string;
  primaryColor?: string;
  logoUrl?: string;
  position?: string;
  greeting?: string;
  assistantName?: string;
  assistantAvatar?: string;
  assistantTagline?: string;
  widgetShape?: string;
  widgetSize?: string;
  widgetAnimation?: string;
  widgetImage?: string;
  welcomeMessage?: string;
  welcomeTimeout?: string;
}

interface AnonSessionResponse {
  jwt: string;
  serviceId: string;
  anonId: string;
  expiresInSec: number;
}

const MOUNT_ID = 'helpdesk-widget-root';
const ANON_STORAGE_PREFIX = 'hd.anon.';

let currentApp: ReturnType<typeof mount> | null = null;

function autoInit(): void {
  if (typeof document === 'undefined') return;

  const script =
    (document.currentScript as HTMLScriptElement | null) ?? findOwnScript();
  if (!script) {
    console.warn(
      '[helpdesk-widget] no <script data-api-key> or <script data-service> tag found',
    );
    return;
  }

  const data = script.dataset as AutoInitDataset;
  const apiKey = trimOrUndef(data.apiKey);
  const backend = trimOrUndef(data.backend) ?? trimOrUndef(data.api);
  const service = trimOrUndef(data.service);
  const jwt = trimOrUndef(data.jwt);

  // Mode B: tenant-signed JWT — boot synchronously.
  if (service && jwt) {
    bootWhenReady(() =>
      boot({
        serviceId: service,
        jwt,
        apiUrl: backend,
        data,
      }),
    );
    return;
  }

  // Mode A: copy-paste — fetch an anonymous JWT, then boot.
  if (apiKey) {
    if (!backend) {
      console.error(
        '[helpdesk-widget] missing data-backend — set it to your dashboard origin (e.g. https://helpdesk.papaya.com.pe)',
      );
      return;
    }
    bootWhenReady(async () => {
      try {
        const session = await fetchAnonSession(backend, apiKey);
        boot({
          serviceId: session.serviceId,
          jwt: session.jwt,
          apiUrl: backend,
          data,
        });
      } catch (err) {
        console.error('[helpdesk-widget] failed to start session', err);
      }
    });
    return;
  }

  console.error(
    '[helpdesk-widget] missing required attributes: provide data-api-key + data-backend (recommended) or data-service + data-jwt + data-api',
  );
}

interface BootInput {
  serviceId: string;
  jwt: string;
  apiUrl?: string;
  data: AutoInitDataset;
}

function boot(input: BootInput): void {
  if (document.getElementById(MOUNT_ID)) return;

  const root = document.createElement('div');
  root.id = MOUNT_ID;
  document.body.appendChild(root);

  currentApp = mount(HelpDeskWidget, {
    target: root,
    props: {
      serviceId: input.serviceId,
      jwt: input.jwt,
      apiUrl: input.apiUrl,
      realtimeUrl: trimOrUndef(input.data.realtime),
      locale: isLocale(input.data.locale) ? input.data.locale : undefined,
      primaryColorOverride: input.data.primaryColor,
      logoUrlOverride: input.data.logoUrl,
      positionOverride: isPosition(input.data.position)
        ? input.data.position
        : undefined,
      greetingOverride: input.data.greeting,
      assistantNameOverride: input.data.assistantName,
      assistantAvatarOverride: input.data.assistantAvatar,
      assistantTaglineOverride: input.data.assistantTagline,
      widgetShapeOverride: isShape(input.data.widgetShape)
        ? input.data.widgetShape
        : undefined,
      widgetSizeOverride: isSize(input.data.widgetSize)
        ? input.data.widgetSize
        : undefined,
      widgetAnimationOverride: isAnimation(input.data.widgetAnimation)
        ? input.data.widgetAnimation
        : undefined,
      widgetImageOverride: input.data.widgetImage,
      welcomeMessageOverride: input.data.welcomeMessage,
      welcomeTimeoutOverride: parseTimeout(input.data.welcomeTimeout),
    },
  });
}

function bootWhenReady(run: () => void | Promise<void>): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => void run(), {
      once: true,
    });
  } else {
    void run();
  }
}

/**
 * POST { apiKey, anonId? } → { jwt, serviceId, anonId, expiresInSec }
 *
 * The anonId is reused from localStorage (per api-key) so a returning
 * visitor lands on the same end-customer record. Errors propagate so the
 * caller can surface them in the console.
 */
async function fetchAnonSession(
  backend: string,
  apiKey: string,
): Promise<AnonSessionResponse> {
  const trimmedBackend = backend.replace(/\/+$/, '');
  const url = `${trimmedBackend}/api/widget/anon-session?service_key=${encodeURIComponent(apiKey)}`;
  const anonId = readAnonId(apiKey);
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ apiKey, anonId }),
    credentials: 'omit',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`anon-session ${res.status}: ${text || res.statusText}`);
  }
  const json = (await res.json()) as { success?: boolean; data?: AnonSessionResponse };
  const data = json?.data;
  if (!data?.jwt || !data?.serviceId) {
    throw new Error('anon-session: malformed response');
  }
  if (data.anonId) writeAnonId(apiKey, data.anonId);
  return data;
}

function readAnonId(apiKey: string): string | undefined {
  try {
    const v = window.localStorage.getItem(ANON_STORAGE_PREFIX + apiKey);
    return v && v.length > 0 ? v : undefined;
  } catch {
    return undefined;
  }
}

function writeAnonId(apiKey: string, anonId: string): void {
  try {
    window.localStorage.setItem(ANON_STORAGE_PREFIX + apiKey, anonId);
  } catch {
    /* private mode / cookies blocked — non-fatal */
  }
}

function trimOrUndef(v: string | undefined): string | undefined {
  if (v === undefined) return undefined;
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

function findOwnScript(): HTMLScriptElement | null {
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[data-api-key], script[data-service]',
  );
  return scripts.length > 0 ? scripts[scripts.length - 1]! : null;
}

function isLocale(v: string | undefined): v is Locale {
  return v === 'en' || v === 'es';
}

function isPosition(v: string | undefined): v is WidgetPosition {
  return (
    v === 'bottom-right' ||
    v === 'bottom-left' ||
    v === 'top-right' ||
    v === 'top-left'
  );
}

function isShape(v: string | undefined): v is WidgetShape {
  return v === 'circle' || v === 'rounded-square' || v === 'pill';
}

function isSize(v: string | undefined): v is WidgetSize {
  return v === 'sm' || v === 'md' || v === 'lg';
}

function parseTimeout(v: string | undefined): number | undefined {
  if (v === undefined || v.length === 0) return undefined;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.min(Math.floor(n), 300);
}

function isAnimation(v: string | undefined): v is WidgetAnimation {
  return (
    v === 'none' ||
    v === 'pulse' ||
    v === 'bounce' ||
    v === 'breath' ||
    v === 'wave'
  );
}

export function destroyHelpDesk(): void {
  if (currentApp) {
    try {
      unmount(currentApp);
    } catch {
      // ignore
    }
    currentApp = null;
  }
  const root = document.getElementById(MOUNT_ID);
  if (root) root.remove();
}

if (typeof window !== 'undefined') {
  (window as unknown as { HelpDesk?: { destroy: () => void } }).HelpDesk = {
    destroy: destroyHelpDesk,
  };
}

autoInit();

export {};
